import { Box, Button, Dialog, DialogTitle, IconButton, MenuItem, Select, Typography, Stack, Tooltip, Divider, Fab, TextField, InputAdornment, DialogContent } from "@mui/material"
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useState } from "react";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import { showSnackbar } from "../../../redux/actions";
import axios from "axios";
import { urlAPI } from "../../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import AddUpdateCompany from "../AddUpdate/AddUpdateCompany";
import CompanyData from "./CompanyData";
import { addUpdateCompany } from "../../../tools/forecast/addUpdateCompany";
import { duplicateCompany } from "../../../tools/forecast/duplicateCompany";

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';

const Company = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    let token = useSelector((state: RootState) => state.user.token);
    let dispatch = useDispatch();
    const params = useParams();

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [searchLabel, setSearchLabel] = useState<string>("");

    const [selectedCompany, setSelectedCompany] = useState<string[]>([]);
    const [editId, setEditId] = useState<string>("");

    const monthList = getForecastMonthList(forecastDates);

    const confirmDelete = async () => {
        try {
            // Utilisation de Promise.all pour gérer les suppressions multiples proprement
            await Promise.all(selectedCompany.map(companyId => 
                axios.delete(`${urlAPI}/forecast/company/${companyId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            ));
            
            setShowDeleteDialog(false);
            setSelectedCompany([]);
            dispatch(showSnackbar("Épargne(s) supprimée(s) avec succès.", "success"));
        } catch (error: any) {
            dispatch(showSnackbar("Erreur lors de la suppression.", "error"));
        }
    }

    const handleAddUpdate = () => {
        setEditId("");
        setOpenModal(true);
    }

    const handleDuplication = () => {
        duplicateCompany(selectedCompany[0], token, setOpenModal, dispatch);
        setSelectedCompany([]);
    }

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- HEADER COHÉRENT --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon sx={{ color: "#0f172a" }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>Entreprise & Épargne</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", bgcolor: "#f1f5f9", px: 1.5, py: 0.5, borderRadius: 1 }}>V{forecastVersion}</Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    <TextField 
                        size="small" 
                        placeholder="Rechercher une épargne..." 
                        value={searchLabel} 
                        onChange={(e) => setSearchLabel(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, minWidth: 300 }}
                        InputProps={{ 
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled"/></InputAdornment> 
                        }}
                    />

                    <Box sx={{ flexGrow: 1 }} />
                    
                    <Button 
                        variant="contained" 
                        startIcon={<AddCircleIcon />} 
                        onClick={handleAddUpdate} 
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3 }}
                    >
                        Ajouter un frais
                    </Button>
                </Box>
            </Box>

            {/* --- TABLEAU (Contenu géré par CompanyData) --- */}
            <Box sx={{ mb: 12 }}>
                <CompanyData
                    forecastVersion={forecastVersion}
                    forecastDates={forecastDates}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    openModal={openModal}
                    showDeleteDialog={showDeleteDialog}
                    setEditId={setEditId}
                    setOpenModal={setOpenModal}
                    searchLabel={searchLabel} // On passe la recherche au composant Data
                />
            </Box>

            {/* --- DOCK FLOTTANT --- */}
            <Box sx={{ 
                position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", 
                display: "flex", alignItems: "center", gap: 2, bgcolor: "white", 
                p: "12px 24px", borderRadius: "100px", border: "1px solid #e2e8f0", 
                boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", zIndex: 1100 
            }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>{selectedCompany.length} sélectionnés</Typography>
                
                <Tooltip title="Supprimer">
                    <Fab color="error" size="small" disabled={selectedCompany.length === 0} onClick={() => setShowDeleteDialog(true)}>
                        <DeleteIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Tooltip title="Dupliquer">
                    <Fab color="info" size="small" disabled={selectedCompany.length !== 1} onClick={handleDuplication} sx={{ color: 'white' }}>
                        <ContentCopyIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                <Fab color="primary" size="medium" onClick={handleAddUpdate}>
                    <AddCircleIcon />
                </Fab>
            </Box>

            {/* MODALE D'AJOUT/MODIF */}
            <Dialog 
                open={openModal} 
                fullWidth maxWidth="xl" 
                PaperProps={{ sx: { borderRadius: 4 } }}
                onClose={(e, reason) => { if(reason !== "backdropClick") setOpenModal(false); }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {editId ? "Modifier le frais" : "Ajouter un frais"}
                    </Typography>
                    <IconButton onClick={() => setShowConfirmDialog(true)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateCompany
                        companyId={editId}
                        handleSubmit={addUpdateCompany}
                        forecastId={params.id}
                        version={forecastVersion}
                        setOpenModal={setOpenModal}
                        monthList={monthList}
                        setShowConfirmDialog={setShowConfirmDialog}
                    />
                </DialogContent>
            </Dialog>

            {/* ALERTES */}
            <AlerteDialog
                open={showConfirmDialog}
                handleClose={() => setShowConfirmDialog(false)}
                deleteProject={() => { setShowConfirmDialog(false); setOpenModal(false); setEditId("") }}
                title="Confirmer l'abandon"
                contentText="Êtes-vous sûr de vouloir abandonner les modifications ?"
                color="error"
            />

            <AlerteDialog
                open={showDeleteDialog}
                handleClose={() => setShowDeleteDialog(false)}
                deleteProject={confirmDelete}
                title="Confirmer la suppression"
                contentText={`Vous allez supprimer ${selectedCompany.length} ligne(s) définitivement.`}
                color="error"
            />
        </Box>
    )
}

export default Company;